type OverlayOptions = {
  /** Whether to show an overlay around the element */
  show?: boolean;
  /** Duration in milliseconds to show the overlay (0 = show indefinitely) */
  duration?: number;
  /** Border color of the overlay */
  borderColor?: string;
  /** Border width in pixels */
  borderWidth?: number;
  /** Background color of the overlay (semi-transparent) */
  backgroundColor?: string;
  /** Border radius in pixels */
  borderRadius?: number;
  /** Z-index of the overlay */
  zIndex?: number;
};

type ScrollOptions = {
  /** Maximum number of retry attempts to find the element */
  maxRetries?: number;
  /** Delay between retry attempts in milliseconds */
  retryDelay?: number;
  /** Initial delay before first scroll attempt in milliseconds */
  initialDelay?: number;
  /** Scroll behavior: 'smooth' or 'auto' */
  behavior?: ScrollBehavior;
  /** Vertical alignment: 'start', 'center', 'end', or 'nearest' */
  block?: ScrollLogicalPosition;
  /** Overlay options for highlighting the element */
  overlay?: OverlayOptions;
};

const DEFAULT_OVERLAY_OPTIONS: Required<OverlayOptions> = {
  show: false,
  duration: 3000,
  borderColor: '#007bff',
  borderWidth: 3,
  backgroundColor: 'rgba(0, 123, 255, 0.1)',
  borderRadius: 4,
  zIndex: 9999,
};

const DEFAULT_OPTIONS: Required<Omit<ScrollOptions, 'overlay'>> & {
  overlay: Required<OverlayOptions>;
} = {
  maxRetries: 15,
  retryDelay: 200,
  initialDelay: 300,
  behavior: 'smooth',
  block: 'center',
  overlay: DEFAULT_OVERLAY_OPTIONS,
};

let overlayElement: HTMLDivElement | null = null;
let overlayCleanup: (() => void) | null = null;

const createOverlay = (
  element: HTMLElement,
  options: Required<OverlayOptions>,
): void => {
  // Remove existing overlay if present
  if (overlayElement) {
    overlayElement.remove();
    if (overlayCleanup) {
      overlayCleanup();
    }
  }

  const overlay = document.createElement('div');
  overlay.style.position = 'fixed';
  overlay.style.pointerEvents = 'none';
  overlay.style.zIndex = String(options.zIndex);
  overlay.style.border = `${options.borderWidth}px solid ${options.borderColor}`;
  overlay.style.backgroundColor = options.backgroundColor;
  overlay.style.borderRadius = `${options.borderRadius}px`;
  overlay.style.transition = 'opacity 0.3s ease-out';
  overlay.style.opacity = '1';

  const updateOverlayPosition = (): void => {
    const rect = element.getBoundingClientRect();
    overlay.style.left = `${rect.left}px`;
    overlay.style.top = `${rect.top}px`;
    overlay.style.width = `${rect.width}px`;
    overlay.style.height = `${rect.height}px`;
  };

  updateOverlayPosition();
  document.body.appendChild(overlay);
  overlayElement = overlay;

  // Update position on scroll and resize
  const handleScroll = (): void => updateOverlayPosition();
  const handleResize = (): void => updateOverlayPosition();

  window.addEventListener('scroll', handleScroll, true);
  window.addEventListener('resize', handleResize);

  overlayCleanup = (): void => {
    window.removeEventListener('scroll', handleScroll, true);
    window.removeEventListener('resize', handleResize);
    if (overlayElement) {
      overlayElement.style.opacity = '0';
      setTimeout(() => {
        if (overlayElement) {
          overlayElement.remove();
          overlayElement = null;
        }
      }, 300);
    }
  };

  // Auto-remove overlay after duration if specified
  if (options.duration > 0) {
    setTimeout(() => {
      if (overlayCleanup) {
        overlayCleanup();
        overlayCleanup = null;
      }
    }, options.duration);
  }
};

/**
 * Scrolls an element into view by its ID.
 * Finds the correct scrollable container and scrolls to center the element.
 *
 * @param id - The ID of the element to scroll to
 * @param options - Configuration options for scrolling behavior
 * @returns A promise that resolves to true if the element was found and scrolled, false otherwise
 */
const scrollElementIntoView = (
  id: string,
  options: ScrollOptions = {},
): Promise<boolean> => {
  const overlayConfig = {
    ...DEFAULT_OVERLAY_OPTIONS,
    ...(options.overlay || {}),
  };
  const config = {
    ...DEFAULT_OPTIONS,
    ...options,
    overlay: overlayConfig,
  };
  let retryCount = 0;

  return new Promise<boolean>(resolve => {
    const attemptScroll = (): void => {
      const targetElement = document.getElementById(id);

      if (targetElement) {
        // Find the scrollable container by walking up the DOM tree
        let scrollContainer: Element | Window = window;
        let element: Element | null = targetElement;

        while (element && element.parentElement) {
          const parent = element.parentElement;
          const style = window.getComputedStyle(parent);
          const hasOverflow =
            style.overflow === 'auto' ||
            style.overflow === 'scroll' ||
            style.overflowY === 'auto' ||
            style.overflowY === 'scroll';

          if (hasOverflow && parent.scrollHeight > parent.clientHeight) {
            scrollContainer = parent;
            break;
          }
          element = parent;
        }

        if (scrollContainer instanceof Element) {
          // Calculate scroll position to center the element
          const containerRect = scrollContainer.getBoundingClientRect();
          const elementRect = targetElement.getBoundingClientRect();
          const relativeTop = elementRect.top - containerRect.top;
          const scrollTop = scrollContainer.scrollTop;
          const containerHeight = scrollContainer.clientHeight;
          const elementHeight = elementRect.height;

          const targetScrollTop =
            scrollTop + relativeTop - containerHeight / 2 + elementHeight / 2;

          scrollContainer.scrollTo({
            top: Math.max(0, targetScrollTop),
            behavior: config.behavior,
          });
        } else {
          // Fallback to window scroll
          targetElement.scrollIntoView({
            behavior: config.behavior,
            block: config.block,
          });
        }

        // Show overlay if enabled
        if (config.overlay.show) {
          // Wait a bit for scroll to complete before showing overlay
          setTimeout(
            () => {
              createOverlay(targetElement, config.overlay);
            },
            config.behavior === 'smooth' ? 500 : 100,
          );
        }

        resolve(true);
        return;
      }

      // Retry if element not found
      retryCount++;
      if (retryCount < config.maxRetries) {
        setTimeout(attemptScroll, config.retryDelay);
      } else {
        resolve(false);
      }
    };

    // Start attempting to scroll after initial delay
    setTimeout(attemptScroll, config.initialDelay);
  });
};

/**
 * Removes the overlay if one is currently displayed.
 * Useful for cleanup or manual removal.
 */
export const removeOverlay = (): void => {
  if (overlayCleanup) {
    overlayCleanup();
    overlayCleanup = null;
  }
};

export default scrollElementIntoView;
